import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Checkbox } from 'components/forms'
import { DataTable } from 'components/DataTable'
import ProgressBar from 'components/ProgressBar/ProgressBar'
import { StyledProgressContainer } from 'components/ProgressBar/styled'
import { isObject } from 'lodash'
import useLocale from 'hooks/useLocale'
import { StyledStatus, StyleParagraphStatus } from 'sections/options/sections/CashRegisters/pages/Hardware/styled'
import { ReactComponent as LaunchFill } from 'media/icons/launchFill.svg'
import DownloadLogs from './DownloadLogs'

const DevicesTable = ({ data, checkedItems, onCheckItem, onCheckAll, isLoading, errorMessage, t, dataRef, appType }) => {
	// const [ tableData, setTableData ] = useState([])
	const [ searchText, setSearchText ] = useState('')
	const [ toogleAllIndicator, setToogleAllIndicator ] = useState(false)
	const locales = useLocale()

	const tableData = data.filter(item => (
		item?.shopAndDeviceName?.toLowerCase?.()?.indexOf?.(searchText) > -1 ||
		item?.hardwareId?.toLowerCase?.()?.indexOf?.(searchText) > -1 ||
		item?.deviceStatus?.toLowerCase?.()?.indexOf?.(searchText) > -1 ||
		item?.appVersion?.toLowerCase?.()?.indexOf?.(searchText) > -1 ||
		item?.lastReportTime?.toLowerCase?.()?.indexOf?.(searchText) > -1
	))

	useEffect(() => {
		if(!isObject(checkedItems)) {
			setToogleAllIndicator(false)
		}
	}, [checkedItems])

	if(dataRef) {
		dataRef.current = tableData
	}

	const handleCheckItem = (deviceHardwareId) => e => {
		if(deviceHardwareId) {
			const val = e?.target?.checked
			if(!val) {
				setToogleAllIndicator(false)
			}
			onCheckItem({ deviceHardwareId, value: val })
		}
	}
	// work with date and time (for lastReportTime field)
	const getComfortableDate = (date) => {
		if (date) {
			return new Date(date).toLocaleString(locales)
		}
	}

	const toogleAll = e => {
		onCheckAll(e.target.checked, tableData)
		setToogleAllIndicator(!toogleAllIndicator)
	}
	const handleSearch = val => {
		if(val) {
			const searchStr = val.trim().toLowerCase()
			setSearchText(searchStr)
		}
	}

	return(
		<div>
			{errorMessage && <AlertMessage>{ errorMessage }</AlertMessage>}
			<DataTable
				id='updateTable'
				columns={[
					{
						Header: t('options_section.strings.name'),
						accessor: 'shopAndDeviceName',
						width: 300,
						Cell: ({ value: cellValue, original }) =>
							(<StyledNameCell>
								<Checkbox
									name={cellValue}
									noForm
									label={cellValue}
									onChange={handleCheckItem(original?.hardwareId)}
									disabled={original?.deviceStatus === 'updating' && true}
									checked={checkedItems[original?.hardwareId]}
								/>
								<a target='_blank' rel='noopener noreferrer' href={`/optionen/kasse/${original?.deviceId}`}><LaunchFill/></a>
							</StyledNameCell>)
					}, {
						Header: t('options_section.strings.serial_number'),
						accessor: 'hardwareId'
					},
					{
						Header: t('options_section.strings.version'),
						accessor: 'appVersion'
					}, {
						Header: t('options_section.fields.tse'),
						accessor: 'isTSEStillLive',
						Cell: ({ value, original }) => (
							<>
								<StyleParagraphStatus color={value ? 'greenColor' : 'errorColor'} fontSize={1}>
									<StyledStatus connect={value}> {value ? t('options_section.strings.status_tse_connect') : t('options_section.strings.status_tse_disconnect')}
										<span>{original.tseRemoteCashRegisterName}</span>
									</StyledStatus>
								</StyleParagraphStatus>
							</>
						),
						minWidth: 200,
						show: locales === 'de'
					}, {
						Header: t('options_section.strings.status'),
						accessor: 'deviceStatus',
						Cell: ({ value, original }) => (<>
							{value && (
								<>
									<StatusIndicator status={value} />
									{t(`options_section.strings.${value}`)}
								</>
							)}
							{value === 'offline' && <StyledLastReport>{getComfortableDate(original?.lastReportTime)}</StyledLastReport>}
						</>),
						width: 220
					}, {
						Header: t('options_section.strings.update_progress'),
						accessor: 'updateProgress',
						width: 180,
						Cell: ({ value, original }) => (<StyledProgressContainer>
							{original?.deviceStatus === 'updating' && <ProgressBar progress={+value} width={120}/>}
						</StyledProgressContainer>)
					},
					{
						Header: t('options_section.strings.permissions'),
						accessor: 'appManagerPermissions',
						Cell: ({ value }) => (typeof value === 'boolean'
							? <Checkbox
								noForm
								// readOnly
								checked={value}
							/>
							: '-'
						), width: 120
					},
					{
						Header: t('options_section.strings.version_app_manager'),
						width: 130,
						accessor: 'appManagerVersion'
					},
					{
						Header: t('options_section.strings.log_file'),
						accessor: 'link',
						width: 260,
						Cell: ({ original }) => (
							<DownloadLogs
								deviceId={original?.deviceId}
								deviceName={original?.deviceName}
								isDisabled={toogleAllIndicator}
							/>
						)
					}
				]}
				withVirtualBody
				height={700}
				itemSize={57}
				TheadComponent={({ className, style, children }) =>
					(
						<>
							<div className={`rt-thead ${className}`} style={style}>
								{children}
							</div>
							<div className={`rt-thead ${className}`} style={{ height: '36px' }}>
								<div className='rt-td'>
									<Checkbox
										noForm
										label={t('options_section.strings.all_shops')}
										checked={toogleAllIndicator}
										onChange={toogleAll}
									/>
								</div>
							</div>
						</>
					)
				}
				data={isLoading ? [] : tableData}
				withSearch
				onSearch={handleSearch}
				loading={isLoading}
			/>
		</div>
	)
}

const StyledNameCell = styled.div`
	overflow: hidden;
	margin-right: 15px;
	> div {
		margin-bottom: 0 !important;
	}
	> a {
		position: absolute;
		right: 0;
		top: 50%;
		transform: translate(0, -50%);
	}
`

const AlertMessage = styled.h2`
	color: #dd5860;
	font-size: 11px;
	margin: 16px 10px 0 20px
`

const StatusIndicator = styled.span`
	display: inline-block;
	margin-right: 6px;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background-color: ${({ status, theme }) => status === 'online' ? theme.greenColor : status === 'offline' ? theme.errorColor : theme.warningColor};
`

const StyledLastReport = styled.span`
	margin-left: auto;
`


export default DevicesTable
